import { API_BASE_URL } from '../config/env';
import {
  getGoogleDriveAudioSrcCandidates,
  getGoogleDriveDirectAudioUrl,
  getGoogleDriveImageSrcCandidates,
  resolveDirectAudioUrl,
  resolveDirectMediaUrl,
} from './video';
import { isMediaOmitted } from './mediaOmitted';
function getApiOrigin() {
  try {
    return new URL(API_BASE_URL).origin;
  } catch {
    return '';
  }
}

/** e.g. API .../lmss/api → "lmss" */
function getApiSlugPrefix() {
  try {
    if (!API_BASE_URL) return '';
    const segments = new URL(API_BASE_URL).pathname.replace(/\/+$/, '').split('/').filter(Boolean);
    if (segments.length >= 2 && segments[segments.length - 1] === 'api') {
      return segments.slice(0, -1).join('/');
    }
    return '';
  } catch {
    return '';
  }
}

function resolveOriginUploadPath(pathWithLeadingSlash) {
  const origin = getApiOrigin();
  if (!origin) return pathWithLeadingSlash;
  const slug = getApiSlugPrefix();
  return slug ? `${origin}/${slug}${pathWithLeadingSlash}` : `${origin}${pathWithLeadingSlash}`;
}

/**
 * True when the string is clearly `hostname/path` (or hostname only) with no scheme.
 * E.g. multer-s3 sometimes returns `blr1.digitaloceanspaces.com/bucket/key` without `https://`.
 * Avoids treating `uploads/foo` as a host (no dot in the first path segment).
 */
export function isSchemelessHttpHostUrl(value) {
  if (!value || /^https?:\/\//i.test(value)) return false;
  const slash = value.indexOf('/');
  const hostPart = slash >= 0 ? value.slice(0, slash) : value;
  if (!hostPart || hostPart.startsWith('.') || !hostPart.includes('.')) return false;
  return /^[a-z0-9]([a-z0-9.-]*[a-z0-9])?$/i.test(hostPart);
}

/** Normalize pasted cloud URLs (add https:// when host/path has no scheme). */
export function normalizeMediaUrlInput(value) {
  const v = String(value || '').trim();
  if (!v) return '';
  if (/^https?:\/\//i.test(v) || v.startsWith('data:') || v.startsWith('blob:')) return v;
  if (v.startsWith('//') && v.length > 2) return `https:${v}`;
  if (isSchemelessHttpHostUrl(v)) return `https://${v}`;
  return v;
}

/**
 * Resolve media URLs saved as relative paths (e.g. /uploads/*, /lms/uploads/*)
 * against backend origin so assets load even when frontend and backend hosts differ.
 */
export function resolveMediaUrl(url) {
  if (!url || typeof url !== 'string' || isMediaOmitted(url)) return '';
  const value = normalizeMediaUrlInput(url);
  if (!value) return '';
  if (value.startsWith('data:') || value.startsWith('blob:')) {
    return value;
  }
  // http(s) — normalize cloud share links (Google Drive /view → direct image URL, Dropbox, etc.)
  if (/^https?:\/\//i.test(value)) {
    return resolveDirectMediaUrl(value);
  }
  if (value.startsWith('/')) {
    if (value.startsWith('/uploads/')) {
      return resolveOriginUploadPath(value);
    }
    const origin = getApiOrigin();
    return origin ? `${origin}${value}` : value;
  }

  // Some uploads are stored as `uploads/...` (no leading `/`).
  if (/^uploads\/|^lms\/uploads\//i.test(value)) {
    return resolveOriginUploadPath(`/${value}`);
  }

  // Slug-prefixed upload paths, e.g. lmss/uploads/... or dhwanisamskritam/uploads/...
  if (/^[a-z0-9-]+\/uploads\//i.test(value)) {
    const origin = getApiOrigin();
    return origin ? `${origin}/${value}` : `/${value}`;
  }

  return value;
}

/**
 * Resolve URLs for audio without converting Drive links to image-oriented lh3 hosts.
 */
function resolveAudioMediaBaseUrl(url) {
  if (!url || typeof url !== 'string' || isMediaOmitted(url)) return '';
  const value = normalizeMediaUrlInput(url);
  if (!value) return '';
  if (value.startsWith('data:') || value.startsWith('blob:')) {
    return value;
  }
  if (/^https?:\/\//i.test(value)) {
    return resolveDirectAudioUrl(value);
  }
  if (value.startsWith('/')) {
    if (value.startsWith('/uploads/')) {
      return resolveOriginUploadPath(value);
    }
    const origin = getApiOrigin();
    return origin ? `${origin}${value}` : value;
  }
  if (/^uploads\/|^lms\/uploads\//i.test(value)) {
    return resolveOriginUploadPath(`/${value}`);
  }
  if (/^[a-z0-9-]+\/uploads\//i.test(value)) {
    const origin = getApiOrigin();
    return origin ? `${origin}/${value}` : `/${value}`;
  }
  return value;
}

/**
 * Ordered image src candidates for <img> (primary resolve + Google Drive fallbacks).
 * Use with referrerPolicy="no-referrer" — Drive often blocks hotlinks that send a Referer.
 */
export function getImageSrcCandidates(url) {
  if (!url || typeof url !== 'string' || isMediaOmitted(url)) return [];
  const primary = resolveMediaUrl(url);
  const drive = getGoogleDriveImageSrcCandidates(url);
  return [...new Set([primary, ...drive].filter(Boolean))];
}

/** Ordered audio src candidates for playback / proxy fetch. */
export function getAudioSrcCandidates(url) {
  if (!url || typeof url !== 'string' || isMediaOmitted(url)) return [];
  const primary = resolvePlayableMediaUrl(url);
  const drive = getGoogleDriveAudioSrcCandidates(url);
  return [...new Set([primary, ...drive].filter(Boolean))];
}

/** Full URL ready for <audio> src (origin + Drive download / Dropbox normalization). */
export function resolvePlayableMediaUrl(url) {
  if (!url || typeof url !== 'string' || isMediaOmitted(url)) return '';
  const driveAudio = getGoogleDriveDirectAudioUrl(url);
  if (driveAudio) return driveAudio;
  return resolveAudioMediaBaseUrl(url);
}

/** Validates user-entered media URLs (http(s), relative paths, cloud host paths, data/blob). */
export function isValidMediaUrl(value) {
  const v = normalizeMediaUrlInput(String(value || '').trim());
  if (!v) return true;
  if (v.startsWith('/') && v.length > 1) return true;
  if (/^uploads\/|^lms\/uploads\//i.test(v)) return true;
  if (v.startsWith('data:') || v.startsWith('blob:')) return true;
  try {
    const u = new URL(v);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}
